/**
 * Classify English holiday names from PH national calendars (e.g. date.nager.at)
 * into labor-law buckets. Names are matched loosely to survive API wording variants.
 *
 * @param {string} name
 * @returns {'regular' | 'special' | 'other'}
 */
export function classifyPhilippineNationalHolidayName(name) {
  const s = String(name || "").trim();
  if (!s) {
    return "other";
  }

  const regularMatchers = [
    /\bnew\s+year'?s\s+day\b/i,
    /\bmaundy\s+thursday\b/i,
    /\bgood\s+friday\b/i,
    /\bday\s+of\s+valor\b/i,
    /\baraw\s+ng\s+kagitingan\b/i,
    /\blabou?r\s+day\b/i,
    /\bindependence\s+day\b/i,
    /\bnational\s+heroes\s+day\b/i,
    /\bbonifacio\s+day\b/i,
    /\bchristmas\s+day\b/i,
    /\brizal\s+day\b/i,
  ];

  const specialMatchers = [
    /\bchinese\s+new\s+year\b/i,
    /\bninoy\s+aquino\b/i,
    /\ball\s+saints\b/i,
    /\bimmaculate\s+conception\b/i,
    /\bchristmas\s+eve\b/i,
    /\blast\s+day\s+of\s+the\s+year\b/i,
    /\bnew\s+year'?s\s+eve\b/i,
  ];

  if (regularMatchers.some((re) => re.test(s))) {
    return "regular";
  }
  if (specialMatchers.some((re) => re.test(s))) {
    return "special";
  }
  return "other";
}

/** Static HR reference (typical occurrence notes; actual dates vary by proclamation/year). */
export const PH_HOLIDAY_LABOR_GUIDE = {
  disclaimer:
    "Dates move yearly for movable observances (Holy Week, lunar New Year, last Monday of August, etc.). The holidays table uses official listings from date.nager.at for the year you select.",
  regularTitle: "🇵🇭 Regular Holidays (with full premium pay)",
  regularIntro:
    "Official Regular Holidays under Philippine labor law — true regular holidays:",
  regularItems: [
    { name: "New Year's Day", typical: "January 1" },
    { name: "Maundy Thursday", typical: "Holy Week (movable)" },
    { name: "Good Friday", typical: "Holy Week (movable)" },
    { name: "Day of Valor", typical: "April 9 (Araw ng Kagitingan)" },
    { name: "Labour Day", typical: "May 1" },
    { name: "Independence Day", typical: "June 12" },
    { name: "National Heroes Day", typical: "Last Monday of August" },
    { name: "Bonifacio Day", typical: "November 30" },
    { name: "Christmas Day", typical: "December 25" },
    { name: "Rizal Day", typical: "December 30" },
  ],
  specialTitle: "⭐ Special (Non-Working) Holidays",
  specialIntro:
    "These are not regular holidays — different pay rules under labor law:",
  specialItems: [
    { name: "Chinese New Year", typical: "Lunar calendar (movable)" },
    { name: "Ninoy Aquino Day", typical: "August 21" },
    { name: "All Saints' Day", typical: "November 1" },
    {
      name: "Feast of the Immaculate Conception of Mary",
      typical: "December 8",
    },
    { name: "Christmas Eve", typical: "December 24" },
    { name: "Last Day of the Year", typical: "December 31" },
  ],
};
